import { faAngleUp, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { Box, Stack } from "@mui/material";
import pluralize from "pluralize";
import { useBoolean } from "usehooks-ts";

import { Icon, IconButton, Typography } from "@/components/ui-v2";

import { DriveUploadQueueItem } from "./DriveUploadQueueItem";
import { UploadQueueItem, useDriveUploadQueueStore } from "./state";

const UPLOAD_CONCURRENCY = 4;

type DriveUploadQueueProps = {
  workspaceId: string;
  hideQueue?: boolean;
  directory: string;
};

export const DriveUploadQueue = (props: DriveUploadQueueProps) => {
  const { workspaceId, hideQueue, directory } = props;
  const { itemsInQueue, reset } = useDriveUploadQueueStore();
  const { value: isExpanded, toggle: toggleExpanded } = useBoolean(true);

  if (itemsInQueue.length === 0) {
    return null;
  }

  let remainingSlots = UPLOAD_CONCURRENCY;

  return (
    <Stack
      sx={{
        position: "fixed",
        right: 32,
        bottom: 0,
        width: 360,
        zIndex: 1250,
        borderRadius: (theme) => theme.spacing(1, 1, 0, 0),
        border: (theme) => `0.5px solid ${theme.palette.secondary["4/50"]}`,
        borderBottom: "none",
        backgroundColor: "secondary.6",
        overflow: "hidden",
        visibility: hideQueue ? "hidden" : "visible",
      }}
    >
      {/* header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        sx={{
          backgroundColor: "secondary.8",
          px: 2,
          py: 2,
        }}
      >
        <DriveUploadQueueTitle itemsInQueue={itemsInQueue} />
        <Stack direction="row" spacing={1}>
          <IconButton
            aria-label="Toggle Upload Queue"
            variant="quaternary"
            size="small"
            onClick={toggleExpanded}
            sx={{
              display: "flex",
              svg: {
                transition: "transform 150ms",
                transformOrigin: "center",
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
              },
            }}
          >
            <Icon icon={faAngleUp} />
          </IconButton>
          <IconButton
            aria-label="Clear Upload Queue"
            variant="quaternary"
            size="small"
            onClick={reset}
            sx={{ display: "flex" }}
          >
            <Icon icon={faXmark} />
          </IconButton>
        </Stack>
      </Stack>

      <Box
        sx={{
          maxHeight: isExpanded ? 288 : 0,
          overflowY: "hidden",
          transition: "max-height 500ms",
        }}
      >
        <Stack
          direction="column"
          sx={{
            maxHeight: 288,
            overflowY: "auto",
          }}
        >
          {itemsInQueue.map((item) => {
            const canUpload =
              remainingSlots > 0 &&
              ["pending", "uploading"].includes(item.status);
            if (canUpload) remainingSlots -= 1;
            return (
              <DriveUploadQueueItem
                key={item.key}
                item={item}
                workspaceId={workspaceId}
                directory={directory}
                canUpload={canUpload}
              />
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};

interface DriveUploadQueueTitleProps {
  itemsInQueue: UploadQueueItem[];
}

const DriveUploadQueueTitle = (props: DriveUploadQueueTitleProps) => {
  const { itemsInQueue } = props;

  const stats = itemsInQueue.reduce(
    (acc, item) => {
      if (item.status === "pending" || item.status === "uploading") {
        acc.uploading++;
      } else if (item.status === "uploaded") {
        acc.uploaded++;
      } else if (item.status === "failed_upload") {
        acc.failed_upload++;
      }
      return acc;
    },
    {
      uploading: 0,
      uploaded: 0,
      failed_upload: 0,
    },
  );

  let title = "Upload Queue";
  if (stats.uploading > 0) {
    title = `Uploading ${stats.uploading} ${pluralize("file", stats.uploading)}`;
  } else if (stats.failed_upload > 0) {
    title = `Failed to upload ${stats.failed_upload} ${pluralize(
      "file",
      stats.failed_upload,
    )}`;
  } else if (stats.uploaded > 0) {
    title = `Uploaded ${stats.uploaded} ${pluralize("file", stats.uploaded)}`;
  }

  return (
    <Typography variant="regular" noWrap title={title}>
      {title}
    </Typography>
  );
};
